import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Brain, RotateCcw, ArrowLeft, Trophy, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemoryGameViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [gameSets, setGameSets] = useState([]);
  const [selectedSetIndex, setSelectedSetIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gameState, setGameState] = useState('ready'); // ready, playing, won
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [lastScores, setLastScores] = useState([]);

  useEffect(() => {
    loadGame();
    loadLastScores();
  }, [id]);

  useEffect(() => {
    // Check if all cards are matched
    if (matched.length === cards.length && cards.length > 0 && gameState === 'playing') {
      handleGameWon();
    }
  }, [matched]);

  const loadGame = async () => {
    try {
      const res = await axios.get(`${API}/memory-games/${id}`);
      setGame(res.data);
      const sets = res.data.sets && res.data.sets.length > 0 ? res.data.sets : [res.data.cards];
      setGameSets(sets);
      initializeCards(sets[0]);
    } catch (error) {
      console.error('Failed to load memory game:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLastScores = async () => {
    try {
      const res = await axios.get(`${API}/game-scores/memory_game/${id}`);
      setLastScores(res.data);
    } catch (error) {
      console.error('Failed to load scores:', error);
    }
  };

  const handleSelectSet = (setIdx) => {
    if (timerInterval) clearInterval(timerInterval);
    setSelectedSetIndex(setIdx);
    initializeCards(gameSets[setIdx]);
    setGameState('ready');
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setTimer(0);
  };

  const initializeCards = (gameCards) => {
    // Create pairs: each card has a term side and definition side
    const cardPairs = [];
    gameCards.forEach((card, idx) => {
      cardPairs.push({ id: `term-${idx}`, type: 'term', content: card.term, pairId: idx });
      cardPairs.push({ id: `def-${idx}`, type: 'definition', content: card.definition, pairId: idx });
    });
    
    // Shuffle cards
    const shuffled = cardPairs.sort(() => Math.random() - 0.5);
    setCards(shuffled);
  };

  const startGame = () => {
    setGameState('playing');
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setTimer(0);
    
    // Start timer
    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };

  const resetGame = () => {
    if (timerInterval) clearInterval(timerInterval);
    initializeCards(gameSets[selectedSetIndex]);
    setGameState('ready');
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setTimer(0);
  };

  const handleCardClick = (cardId) => {
    if (gameState !== 'playing') return;
    if (flipped.includes(cardId)) return;
    if (matched.includes(cardId)) return;
    if (flipped.length >= 2) return;

    const newFlipped = [...flipped, cardId];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      checkMatch(newFlipped);
    }
  };

  const checkMatch = (flippedCards) => {
    const card1 = cards.find(c => c.id === flippedCards[0]);
    const card2 = cards.find(c => c.id === flippedCards[1]);

    if (card1.pairId === card2.pairId) {
      // Match found!
      setTimeout(() => {
        setMatched([...matched, card1.id, card2.id]);
        setFlipped([]);
      }, 500);
    } else {
      // No match
      setTimeout(() => {
        setFlipped([]);
      }, 1000);
    }
  };

  const handleGameWon = async () => {
    if (timerInterval) clearInterval(timerInterval);
    setGameState('won');

    // Save score
    try {
      await axios.post(`${API}/game-scores`, {
        game_type: 'memory_game',
        game_id: id,
        score: timer, // Lower time is better
        time_taken: timer
      });
      loadLastScores();
    } catch (error) {
      console.error('Failed to save score:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B9D] mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!game) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-red-600">Memory game not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate('/memory-game-center')}
          className="flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Memory Game Center</span>
        </button>

        <div className="bg-gradient-to-r from-pink-500 to-red-500 text-white p-8 rounded-lg mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Brain className="w-12 h-12" />
            <div>
              <h1 className="text-4xl font-bold retro-title">{game.test_title}</h1>
              <p className="text-lg opacity-90">Memory Matching Game</p>
            </div>
          </div>
        </div>

        {/* Set Selector */}
        {gameSets.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="memory-set-selector">
            {gameSets.map((_, setIdx) => (
              <button
                key={setIdx}
                onClick={() => handleSelectSet(setIdx)}
                className={`px-4 py-2 rounded-lg font-bold transition-all ${
                  selectedSetIndex === setIdx
                    ? 'bg-[#FF6B9D] text-white scale-105 shadow-lg'
                    : 'bg-pink-100 text-[#2C1810] hover:bg-pink-200'
                }`}
                data-testid={`memory-set-button-${setIdx}`}
              >
                Set {setIdx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Game Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border-4 border-pink-500 p-4 rounded-lg text-center">
            <Clock className="w-8 h-8 text-pink-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{formatTime(timer)}</div>
            <div className="text-sm text-gray-600">Time</div>
          </div>
          <div className="bg-white border-4 border-red-500 p-4 rounded-lg text-center">
            <Trophy className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{moves}</div>
            <div className="text-sm text-gray-600">Moves</div>
          </div>
          <div className="bg-white border-4 border-pink-600 p-4 rounded-lg text-center">
            <Brain className="w-8 h-8 text-pink-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{matched.length / 2} / {gameSets[selectedSetIndex]?.length || 0}</div>
            <div className="text-sm text-gray-600">Pairs Matched</div>
          </div>
        </div>

        {/* Game Controls */}
        {gameState === 'ready' && (
          <div className="text-center mb-8">
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-pink-500 to-red-500 text-white px-12 py-4 rounded-lg font-bold text-xl hover:shadow-xl transition-all transform hover:scale-105"
            >
              Start Game
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div className="text-center mb-8 hidden md:block">
            <button
              onClick={resetGame}
              className="flex items-center gap-2 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all mx-auto"
            >
              <RotateCcw className="w-5 h-5" />
              Reset Game
            </button>
          </div>
        )}

        {/* Win Screen */}
        {gameState === 'won' && (
          <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white p-8 rounded-lg mb-8 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold retro-title mb-4">🎉 You Won! 🎉</h2>
            <p className="text-2xl mb-2">Time: {formatTime(timer)}</p>
            <p className="text-xl mb-6">Moves: {moves}</p>
            <button
              onClick={resetGame}
              className="bg-white text-green-600 px-8 py-3 rounded-lg font-bold hover:shadow-xl transition-all transform hover:scale-105"
            >
              Play Again
            </button>
          </div>
        )}

        {/* Memory Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id) || matched.includes(card.id);
            const isMatched = matched.includes(card.id);

            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                disabled={gameState !== 'playing'}
                className={`relative h-48 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                  gameState !== 'playing' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                style={{
                  transformStyle: 'preserve-3d',
                  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
              >
                {/* Card Back (blank) */}
                <div
                  className={`absolute inset-0 backface-hidden rounded-lg shadow-lg ${
                    isMatched ? 'bg-green-400 border-4 border-green-600' : 'bg-gradient-to-br from-pink-400 to-red-400 border-4 border-pink-600'
                  } flex items-center justify-center`}
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <Brain className="w-16 h-16 text-white opacity-50" />
                </div>

                {/* Card Front (content) */}
                <div
                  className={`absolute inset-0 backface-hidden rounded-lg shadow-lg ${
                    isMatched ? 'bg-green-100 border-4 border-green-500' : 'bg-white border-4 border-gray-300'
                  } p-4 flex items-center justify-center`}
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)'
                  }}
                >
                  <div className="text-center w-full h-full flex flex-col items-center justify-center p-2">
                    <div className={`text-xs font-bold mb-1 ${card.type === 'term' ? 'text-pink-600' : 'text-blue-600'}`}>
                      {card.type === 'term' ? 'TERM' : 'DEFINITION'}
                    </div>
                    <p className="text-xs md:text-base font-semibold text-gray-800 line-clamp-4 break-words overflow-hidden">
                      {card.content}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Mobile Reset Button - at bottom */}
        {gameState === 'playing' && (
          <div className="md:hidden text-center mb-8">
            <button
              onClick={resetGame}
              className="flex items-center gap-2 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all mx-auto w-full justify-center"
            >
              <RotateCcw className="w-5 h-5" />
              Reset Game
            </button>
          </div>
        )}

        {/* Last 3 Scores */}
        {lastScores.length > 0 && (
          <div className="bg-white border-4 border-pink-500 p-6 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-pink-500" />
              Your Last 3 Scores
            </h3>
            <div className="space-y-2">
              {lastScores.map((score, idx) => (
                <div key={score.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                  <span className="font-semibold text-gray-700">Attempt {idx + 1}</span>
                  <span className="text-pink-600 font-bold">{formatTime(score.time_taken)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
